import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MarkdownContent } from "./Markdown";

const AVERAGE_CHARACTERS_PER_LINE = 80;

interface ExpandableTextProps {
  text: string;
  maxLines?: number;
}

function ExpandableText({ text, maxLines = 3 }: ExpandableTextProps) {
  const { t } = useTranslation(["commons"]);
  const [isExpanded, setIsExpanded] = useState(false);

  const { exceedsMaxLines, truncatedText } = useMemo(() => {
    const lines = text.split("\n");
    const maxCharacters = maxLines * AVERAGE_CHARACTERS_PER_LINE;

    const exceedsLimits =
      lines.length > maxLines || text.length > maxCharacters;

    if (!exceedsLimits) {
      return { exceedsMaxLines: false, truncatedText: text };
    }

    const linesTruncated = lines.slice(0, maxLines).join("\n");
    const finalTruncated =
      linesTruncated.length > maxCharacters
        ? linesTruncated.substring(0, maxCharacters)
        : linesTruncated;

    return { exceedsMaxLines: true, truncatedText: finalTruncated };
  }, [text, maxLines]);

  const toggleExpansion = () => setIsExpanded(!isExpanded);

  const toggleButtonText = isExpanded ? t("showLess") : t("showMore");
  const displayText = isExpanded ? text : truncatedText;

  return (
    <div className={"prose-markdown no-y-margin"}>
      <div id="expandable-text" className="text-sm md:text-base">
        <MarkdownContent shouldReplaceMathDelimiter={false}>
          {displayText}
        </MarkdownContent>
      </div>
      {exceedsMaxLines && (
        <button
          type="button"
          className="cursor-pointer text-neutral-content/70 text-xs hover:underline md:text-sm"
          onClick={toggleExpansion}
          aria-expanded={isExpanded}
          aria-controls="expandable-text"
        >
          {toggleButtonText}
        </button>
      )}
    </div>
  );
}

export default ExpandableText;
